/**
 * Interaction scheduling helpers used to keep INP low.
 *
 * The pattern: handle the *urgent* part of an interaction synchronously
 * (visual feedback / focus), then yield to the browser so it can paint,
 * and run the expensive part afterwards.
 */

type Scheduler = {
  yield?: () => Promise<void>;
  postTask?: (cb: () => void, opts?: { priority?: string }) => Promise<unknown>;
};

function scheduler(): Scheduler | undefined {
  return (globalThis as unknown as { scheduler?: Scheduler }).scheduler;
}

/** Yields to the main thread so the browser can paint before more work. */
export function yieldToMain(): Promise<void> {
  const s = scheduler();
  if (s?.yield) return s.yield();
  return new Promise((resolve) => setTimeout(resolve, 0));
}

/** Runs non-urgent work after the next paint (or during idle time). */
export function runNonUrgent(cb: () => void): void {
  if (typeof window === "undefined") {
    cb();
    return;
  }
  const s = scheduler();
  if (s?.postTask) {
    void s.postTask(cb, { priority: "background" });
    return;
  }
  const ric = (window as unknown as { requestIdleCallback?: (c: () => void, o?: unknown) => void })
    .requestIdleCallback;
  if (ric) ric(cb, { timeout: 500 });
  else setTimeout(cb, 0);
}

/** Defers work until after the next frame is painted. */
export function afterPaint(cb: () => void): void {
  if (typeof window === "undefined") {
    cb();
    return;
  }
  requestAnimationFrame(() => setTimeout(cb, 0));
}
